import { useCallback } from 'react';
import useSWR, { type Fetcher } from 'swr';

import {
  maintenanceVisitPeriodsClearNotPerformedCreate,
  maintenanceVisitPeriodsList,
  maintenanceVisitPeriodsMarkNotPerformedCreate,
  maintenanceVisitPeriodsSummaryRetrieve,
} from '~/data/generated/endpoints/maintenance-visit-periods/maintenance-visit-periods';
import type { MaintenanceVisitPeriodsListParams } from '~/data/generated/model';
import { parseApiResponse } from '~/data/parseApiResponse';
import {
  type MaintenanceVisitPeriod,
  maintenanceVisitPeriodListSchema,
  type MaintenanceVisitSummaryResponse,
  maintenanceVisitSummarySchema,
} from '~/data/schemas/maintenanceVisit';

export type VisitPeriodScopeTarget = { property: string } | { car: string } | { contract: string };

export type VisitPeriodScope = VisitPeriodScopeTarget & { start_date?: string; end_date?: string };

type Key = readonly ['maintenance-visit-periods', MaintenanceVisitPeriodsListParams];

const fetcher: Fetcher<MaintenanceVisitPeriod[], Key> = async ([, params]) => {
  const { data } = await maintenanceVisitPeriodsList(params);
  return parseApiResponse(maintenanceVisitPeriodListSchema, data, 'MaintenanceVisitPeriods');
};

export function useMaintenanceVisitPeriods(scope: VisitPeriodScope | null) {
  const key: Key | null = scope ? ['maintenance-visit-periods', scope] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    MaintenanceVisitPeriod[],
    Error,
    Key | null
  >(key, fetcher);

  const markNotPerformed = useCallback(
    async (carId: string, periodStart: string, note = '') => {
      await maintenanceVisitPeriodsMarkNotPerformedCreate({
        carId,
        periodStart,
        note,
      });
      await mutate();
    },
    [mutate],
  );

  const clearNotPerformed = useCallback(
    async (carId: string, periodStart: string) => {
      await maintenanceVisitPeriodsClearNotPerformedCreate({ carId, periodStart });
      await mutate();
    },
    [mutate],
  );

  return {
    periods: data ?? [],
    isLoading,
    isValidating,
    error,
    markNotPerformed,
    clearNotPerformed,
    refresh: mutate,
  };
}

type SummaryKey = readonly ['maintenance-visit-summary', MaintenanceVisitPeriodsListParams];

const summaryFetcher: Fetcher<MaintenanceVisitSummaryResponse, SummaryKey> = async ([, params]) => {
  const { data } = await maintenanceVisitPeriodsSummaryRetrieve(params);
  return parseApiResponse(maintenanceVisitSummarySchema, data, 'MaintenanceVisitSummary');
};

export function useMaintenanceVisitSummary(scope: VisitPeriodScope | null) {
  const key: SummaryKey | null = scope ? ['maintenance-visit-summary', scope] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    MaintenanceVisitSummaryResponse,
    Error,
    SummaryKey | null
  >(key, summaryFetcher, {
    // The window is part of the SWR key, so without this the table empties between
    // windows, which reads as the data having gone away.
    keepPreviousData: true,
  });

  return {
    summaries: data?.cars ?? [],
    window: data?.window ?? null,
    isLoading,
    isValidating,
    error,
    refresh: mutate,
  };
}
